import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toggleMenu } from "../../../../utils/helpers.js";
import NotificationBadge from "../../ui/notification-badge/notification-badge.tsx";
import Cookies from "js-cookie";
import { getApi } from "../../../../utils/api.js";
import { API_GET_PENDING_LEAVE_REQUESTS } from "../../../../config/Endpoints.js";
import { useEvent } from "../../../../Hooks/useEvent.js";
import { Event } from "../../../../config/events.js";
import styles from "./side-navbar.module.css";
import {
  LayoutDashboard,
  User,
  Activity,
  CheckSquare,
  Users,
  BarChart3,
  HeartCrack,
  Briefcase,
  ChevronDown,
  Settings,
  type LucideIcon,
  Megaphone,
  Calendar,
  ClipboardClock,
  HandCoins,
  ShieldCheck,
  BadgeDollarSign,
  UserCog,
  UserRound,
  Luggage,
  ClipboardCheck,
  CalendarDays,
  Receipt,
  Headphones,
  FolderKanban,
  UsersRound,
  Gauge,
  Layers3,
  FileCheck2,
  MailPlus,
  ScanEye,
  TrendingUp,
} from "lucide-react";

import {
  hasNavigableSidebarRoute,
  type SidebarMenuItem as MenuItem,
  type SidebarSubMenuItem as SubMenuItem,
} from "./normalize-employee-sidebar-menu.ts";
import {
  formatNavigationLabel,
  isRestrictedForSQELabsMenu,
  isRestrictedForSQELabsPath,
} from "../../../utils/sqe-labs-navigation-access.ts";
import { useSqeLabs } from "../../../hooks/use-sqe-labs.ts";
import { isActiveSidebarRoute } from "./active-sidebar-route.ts";
import { usePerformanceLensAccess } from "../../../lib/performance-lens/use-access.ts";
import { getPerformanceLensWorkspaceDestination } from "../../../lib/performance-lens/workspace-navigation.ts";

interface LeaveDataItem {
  childName: string;
  parentName: string;
  count: number;
}

interface SideNavbarProps {
  menu: MenuItem[];
}

const CAELIUS_POLICIES_MENU_NAME = "Caelius Policies";
const CAELIUS_CONNECT_MENU_NAME = "Caelius Connect";
const SQE_CONNECT_MENU_NAME = "SQE Connect";

function sidebarDestination(path: string | undefined): string {
  return `/dashboard${getPerformanceLensWorkspaceDestination(path) ?? path ?? ""}`;
}

function isActiveDestination(destination: string | null, pathname: string, search: string): boolean {
  if (!destination) return false;
  return destination.includes("?")
    ? `${pathname}${search}` === destination
    : isActiveSidebarRoute(pathname, destination);
}

const MENU_ICONS: Record<string, LucideIcon> = {
  Dashboard: LayoutDashboard,
  "My Profile": User,
  "My Workspace": Briefcase,
  Performance: Activity,
  "Action Center": CheckSquare,
  "Project TeamFlow": Users,
  "My Projects": Users,
  TeamFlow: Users,
  "Team Management": Users,
  "Caelius Connect": UsersRound,
  "Analytics & Insights": BarChart3,
  "Growth & Initatives": TrendingUp,
  "Growth & Initiatives": TrendingUp,
  "Utilization Report": Gauge,
  "Employee Offboarding": HeartCrack,
  "Exit Management": HeartCrack,
  "Initiate Offboarding": HeartCrack,
  "Initiate Offboarding Request": HeartCrack,
  Announcements: Megaphone,
  "Asset Management": Settings,
  "Attendance & Leaves": Calendar,
  "Attendance & Leave": Calendar,
  Workday: Calendar,
  Timesheets: ClipboardClock,
  Projects: FolderKanban,
  Project: FolderKanban,
  Assessment: ClipboardCheck,
  Events: CalendarDays,
  "IT Requests": Headphones,
  Reimbursements: Receipt,
  "Expense Claims": HandCoins,
  "Employee Onboarding": UserRound,
  "Salary Management": BadgeDollarSign,
  "Role Management": UserCog,
  "Travel Management": Luggage,
  "Caelius' Policies": ShieldCheck,
  "Tech Skills & Certifications": Layers3,
  "Appraisal Hub": FileCheck2,
  "Adhoc Email Setup": MailPlus,
  "Performance Lens": ScanEye,
  [CAELIUS_POLICIES_MENU_NAME]: ShieldCheck,
  "SQE Policies": ShieldCheck,
  [CAELIUS_CONNECT_MENU_NAME]: Users,
  [SQE_CONNECT_MENU_NAME]: Users,
};

const NORMALIZED_MENU_ICONS = new Map(
  Object.entries(MENU_ICONS).map(([name, Icon]) => [
    name.trim().replace(/\s+/g, " ").toLowerCase(),
    Icon,
  ]),
);

function renderNavIcon(itemName: string, achorValue?: string): React.ReactNode {
  if (achorValue?.toLowerCase() === "/initiateresignation") {
    return <HeartCrack size={18} strokeWidth={1.8} />;
  }
  if (isCaeliusConnectMenu(itemName)) {
    return <UsersRound size={18} strokeWidth={1.8} />;
  }
  const Icon = NORMALIZED_MENU_ICONS.get(
    itemName.trim().replace(/\s+/g, " ").toLowerCase(),
  );
  if (Icon) return <Icon size={18} strokeWidth={1.8} />;
  return FALLBACK_ICON;
}

function isCaeliusConnectMenu(itemName: string): boolean {
  return itemName.trim().toLowerCase() === "caelius connect";
}

const FALLBACK_ICON = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <ChevronDown
      size={14}
      strokeWidth={1.8}
      className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
    />
  );
}


const NotifyDot = () => <span className={styles.notifyDot} aria-hidden />;

export default function SideNavbar({ menu }: SideNavbarProps) {
  const [menuList, setMenuList] = useState<MenuItem[]>([]);
  const { pathname, search } = useLocation();
  const [activeSubMenu, setActiveSubMenu] = useState<number | null>(null);
  const lastSyncedPath = useRef<string | null>(null);
  const [leaveData, setLeaveData] = useState<LeaveDataItem[]>([]);
  const accessToken = Cookies.get("accessToken");
  const isSQEEmployee = useSqeLabs();
  const isSQELabsUser = isSQEEmployee;
  const loggedInEmployeeTypes = isSQEEmployee ? ["SQELABS"] : [];
  const { access: performanceLensAccess } = usePerformanceLensAccess();

  const preprocessMenuList = (source: MenuItem[]): MenuItem[] => {
    const extraSubItems: SubMenuItem[] = [
      { name: "Attendance Report", achorValue: "/exportAttendance" },
      { name: "Reimbursement Report", achorValue: "/downloadReimbursement" },
      { name: "Utilization Report", achorValue: "/utilizationreport" },
    ];
    return source
      .filter(
        (item) =>
          (!isSQELabsUser ||
            (!isRestrictedForSQELabsMenu(item.name) &&
              !isRestrictedForSQELabsPath(item.achorValue))),
      )
      .map((item) => {
      const itemName = formatNavigationLabel(
        item.name,
        loggedInEmployeeTypes,
      ) ?? item.name;
      const subMenu = item.subMenu
        ?.filter(
          (sub) =>
            Boolean(sub.achorValue?.trim()) &&
            sub.achorValue !== "/performanceConfiguration" &&
            (sub.achorValue !== "/adminDashboard" || performanceLensAccess?.admin === true) &&
            (sub.achorValue !== "/hodDashboard" || !!performanceLensAccess?.hodProjects?.length) &&
            (!isSQELabsUser ||
              (!isRestrictedForSQELabsMenu(sub.name) &&
                !isRestrictedForSQELabsPath(sub.achorValue))),
        )
        .map((sub) => ({
          ...sub,
          name: formatNavigationLabel(
            sub.name,
            loggedInEmployeeTypes,
          ) ?? sub.name,
        }));

      if (item.name === "Analytics & Insights" && Array.isArray(item.subMenu)) {
        const updated = [...(subMenu ?? item.subMenu)];
        extraSubItems.forEach((s) => {
          if (
            isSQELabsUser &&
            (isRestrictedForSQELabsMenu(s.name) ||
              isRestrictedForSQELabsPath(s.achorValue))
          ) return;
          if (!updated.some((e) => e.achorValue === s.achorValue))
            updated.push(s);
        });
        return { ...item, name: itemName, subMenu: updated };
      }
      return { ...item, name: itemName, subMenu };
      })
      .filter(hasNavigableSidebarRoute);
  };

  const mergeLeaveData = (list: MenuItem[]): MenuItem[] =>
    list.map((item) => {
      let hasNotification = false;
      const updatedSubMenu: SubMenuItem[] =
        item.subMenu?.map((sub) => {
          const found = leaveData.find(
            (ld) => ld.childName === sub.name && ld.parentName === item.name,
          );
          if (found && found.count > 0) {
            hasNotification = true;
            return { ...sub, notificationValue: found.count };
          }
          return sub;
        }) ?? [];
      return { ...item, subMenu: updatedSubMenu, hasNotification };
    });

  const fetchLeaveData = () => {
    getApi<{ data: LeaveDataItem[] }>(
      API_GET_PENDING_LEAVE_REQUESTS,
      accessToken,
    )
      .then((res) => setLeaveData(res.data.data))
      .catch(console.error);
  };

  useEffect(() => {
    fetchLeaveData();
  }, []);

  useEvent(Event.SIDEBAR.NOTIFY_EVENT, fetchLeaveData);

  useEffect(() => {
    if (!menu?.length) return;
    const processed = preprocessMenuList(menu);

    const pIdx = processed.findIndex((i) => i.name === "Project TeamFlow");
    if (pIdx !== -1 && pIdx !== 3) {
      const [item] = processed.splice(pIdx, 1);
      processed.splice(3, 0, item);
    }

    const oIdx = processed.findIndex((i) =>
      i.name === "Employee Offboarding" || i.name === "Exit Management",
    );
    if (oIdx > 0) {
      const [item] = processed.splice(oIdx, 1);
      processed.splice(oIdx - 1, 0, item);
    }

    setMenuList(mergeLeaveData(processed));
  }, [menu, leaveData, isSQEEmployee, isSQELabsUser, performanceLensAccess]);

  useEffect(() => {
    const locationKey = `${pathname}${search}`;
    if (!menuList.length || lastSyncedPath.current === locationKey) return;
    lastSyncedPath.current = locationKey;
    const activeIndex = menuList.findIndex((item) =>
      item.subMenu?.some((sub) =>
        isActiveDestination(sidebarDestination(sub.achorValue), pathname, search),
      ),
    );
    setActiveSubMenu(activeIndex === -1 ? null : activeIndex);
  }, [menuList, pathname, search]);

  return (
    <aside>
      <nav className={styles.sidebar}>
        <div id="hideMenuBtn" className="close-button" onClick={toggleMenu}>
          <span></span>
          <span></span>
        </div>

        <div className={styles.logoWrapper}>
          <a href="/dashboard/" className="text-left ">
            <img
              src={
                isSQEEmployee
                  ? "/assets/sqe-login/sqe_logo.svg"
                  : "/assets/CaeliusOne_logo.svg"
              }
              alt={isSQEEmployee ? "SQE Labs" : "CaeliusOne"}
              className={`${styles.brandLogo} ${isSQEEmployee ? styles.sqeBrandLogo : ""}`}
            />
          </a>
        </div>
        <div className={styles.divider}></div>
        <div className={`${styles.scrollbarCompact} ${styles.navWrapper}`}>
          <ul
            className={`${styles.navList} d-flex flex-column gap-1 m-0 list-unstyled`}
          >
            {menuList.map((item, index) => {
              const hasSubMenu =
                Array.isArray(item.subMenu) && item.subMenu.length > 0;
              const directSubMenu =
                item.showMenu === 0 && item.subMenu?.length === 1
                  ? item.subMenu[0]
                  : null;
              const mainPath = directSubMenu
                ? sidebarDestination(directSubMenu.achorValue)
                : hasSubMenu ? null : sidebarDestination(item.achorValue);
              const isActive = isActiveDestination(mainPath, pathname, search);
              const isOpen = activeSubMenu === index;

              return (
                <li key={index} className={`list-unstyled ${styles.navItem}`}>
                  {hasSubMenu && !directSubMenu ? (
                    <button
                      className={`d-flex align-items-center w-100 box-border  rounded gap-3 
text-decoration-none text-start border-0 bg-transparent 
 fw-medium ${styles.navRow} ${isOpen ? styles.navRowOpen : ""}`}
                      onClick={() =>
                        setActiveSubMenu((p) => (p === index ? null : index))
                      }
                      aria-expanded={isOpen.toString()}
                    >
                      <span className={styles.navIcon}>
                        {renderNavIcon(item.name, item.achorValue)}
                      </span>
                      <span className={styles.navLabel} title={item.name}>
                        {item.name}
                      </span>
                      {item.hasNotification && <NotifyDot />}
                      <ChevronIcon open={isOpen} />
                    </button>
                  ) : (
                    <Link
                      to={mainPath!}
                      className={`d-flex gap-3 ${styles.navRow} ${isActive ? styles.navRowActive : ""}`}
                    >
                      <span className={styles.navIcon}>
                        {renderNavIcon(item.name, directSubMenu?.achorValue ?? item.achorValue)}
                      </span>
                      <span className={styles.navLabel}>
                        {directSubMenu?.name ?? item.name}
                      </span>
                      {directSubMenu?.notificationValue && directSubMenu.notificationValue > 0 && (
                        <NotificationBadge count={directSubMenu.notificationValue} />
                      )}
                    </Link>
                  )}

                  {hasSubMenu && !directSubMenu && isOpen && (
                    <ul className={styles.subList}>
                      {item.subMenu!.map((sub, subIdx) => {
                        const subPath = sidebarDestination(sub.achorValue);
                        const isActiveSub = isActiveDestination(subPath, pathname, search);
                        return (
                          <li key={subIdx}>
                            <Link
                              to={subPath}
                              className={`d-flex gap-3 ${styles.subLink} ${isActiveSub ? styles.subLinkActive : ""}`}
                            >
                              {isCaeliusConnectMenu(sub.name) ? (
                                <span className={styles.navIcon}>
                                  {renderNavIcon(sub.name)}
                                </span>
                              ) : null}
                              <span
                                className={styles.subLabel}
                                title={sub.name}
                              >
                                {sub.name}
                              </span>
                              {sub.notificationValue &&
                                sub.notificationValue > 0 && (
                                  <NotificationBadge
                                    count={sub.notificationValue}
                                  />
                                )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </aside>
  );
}
